import { useEffect, useMemo, useState } from 'react'

import { Badge, BadgeType, Member } from '@tribeplatform/gql-client/types'
import { MemberAvatarPlaceholder } from '@tribeplatform/react-components/common/components/member'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  MemberBadgeLocation,
  MemberBadges as MemberBadgesList,
} from '@tribeplatform/react-components/MemberBadge'
import {
  useAuthToken,
  useNetwork,
  useReorderBadges,
} from '@tribeplatform/react-sdk/hooks'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'
import { BackgroundProvider } from '@tribeplatform/react-ui-kit/BackgroundContext'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { List } from '@tribeplatform/react-ui-kit/Layout'
import { PageHeader } from '@tribeplatform/react-ui-kit/PageHeader'
import {
  DragResult,
  SortableList,
} from '@tribeplatform/react-ui-kit/SortableList'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { MemberAbout } from '../../Member/MemberAbout.js'

import { BadgeModal } from './BadgeModal.js'
import { BadgeRow } from './BadgeRow.js'
import { MemberPostsPlaceholder } from './MemberPostsPlaceholder.js'

export const MemberBadges = () => {
  const { $t } = useI18n()
  const {
    data: { member },
  } = useAuthToken()
  const {
    data: { badges },
  } = useNetwork()
  const [badgeModal, setBadgeModal] = useState<Badge>(null)
  const [activeBadges, setActiveBadges] = useState(badges.filter(b => b.active))
  const { mutateAsync: reorderBadges } = useReorderBadges()
  const fakeMember = useMemo(
    () =>
      ({
        id: member?.id,
        name: 'John Smith',
        badges: activeBadges.map(badge => ({
          badgeId: badge.id,
          badge,
        })),
      }) as Member,
    [activeBadges, member],
  )
  const disabledBadges = useMemo(
    () =>
      badges.filter(
        badge => !badge.active && badge.type !== BadgeType.SuspendedMember,
      ),
    [badges],
  )
  useEffect(() => {
    setActiveBadges(badges.filter(b => b.active))
  }, [badges])

  const onDragEnd = async (result: DragResult) => {
    if (!result.destination) {
      return
    }

    const reorderedBadges = SortableList.reorder(
      activeBadges,
      result.source.index,
      result.destination.index,
    )
    setActiveBadges(reorderedBadges)

    try {
      await reorderBadges({
        input: {
          badgeOrderIds: reorderedBadges.map(({ id }) => id),
        },
      })
    } catch (error) {
      setActiveBadges(activeBadges)
      toast({
        title: $t({
          defaultMessage: 'An error occurred while reordering badges',
          id: 'Admin.MemberBadges.ErrorReorderingBadges',
        }),
        status: 'error',
      })
    }
  }

  return (
    <div className="grid grid-cols-12 h-screen">
      <div className="web:col-span-12 web:lg:col-span-4 bg-surface p-5 flex flex-col space-y-5 text-content-subdued max-h-screen overflow-auto">
        <BackgroundProvider backgroundType="surface">
          <div className="flex items-center space-s-2">
            <h4 className="text-lg font-medium flex-grow">
              <T defaultMessage="Badges" id="Generics.Badges" />
            </h4>
            <Button
              variant="secondaryNeutral"
              leadingIcon={<SvgIcon name="plus" />}
              size="md"
              className="flex-shrink-0"
              onClick={() => {
                setBadgeModal({} as Badge)
              }}
            >
              <T defaultMessage="Add badge" id="Generics.AddBadge" />
            </Button>
          </div>
          <div>
            <SortableList
              id="member-badges"
              onDragEnd={onDragEnd}
              items={activeBadges}
              renderItem={badge => (
                <SortableList.Item id={badge.id}>
                  <BadgeRow
                    key={badge.id}
                    badge={badge}
                    member={member}
                    onClick={setBadgeModal}
                    dragHandle={<SortableList.Handle />}
                  />
                </SortableList.Item>
              )}
            />
            {!activeBadges?.length && (
              <p>
                <T
                  defaultMessage="There are no enabled badges."
                  id="Generics.NoEnabledBadges"
                />
              </p>
            )}
          </div>
          <h4>
            <T defaultMessage="Disabled badges" id="Generics.DisabledBadges" />
          </h4>
          <List spacing="xs">
            {disabledBadges?.map(badge => (
              <List.Item key={badge.id} className="group">
                <BadgeRow
                  key={badge.id}
                  badge={badge}
                  member={member}
                  onClick={setBadgeModal}
                />
              </List.Item>
            ))}
            {!disabledBadges?.length ? (
              <p>
                <T
                  defaultMessage="There are no disabled badges."
                  id="Generics.NoDisabledBadges"
                />
              </p>
            ) : null}
          </List>
        </BackgroundProvider>
      </div>
      <div className="hidden lg:flex col-span-8 p-5 flex-col space-y-5 max-h-screen overflow-auto">
        <Alert status="info" className="shadow">
          <T
            defaultMessage='Below you can see a preview of a {MEMBER} with all active badges. To assign a badge to a specific {MEMBER} you can go to their profile and under "..." click on "Manage badges".'
            id="Admin.MemberBadges.PreviewNotice"
          />
        </Alert>
        <div className="flex-shrink-0">
          <Card className="overflow-hidden -mt-5 sm:mt-0">
            <PageHeader
              title={
                <div className="flex items-center space-s-2">
                  <div className="truncate shrink-0 max-w-[150px]">
                    <T
                      defaultMessage="John Smith"
                      id="Admin.ManageBadges.Preview.NameSample"
                    />
                  </div>
                  <MemberBadgesList
                    member={fakeMember}
                    location={MemberBadgeLocation.ProfileName}
                    size="sm"
                  />
                </div>
              }
              backgroundImage={<div className="h-32" />}
              avatar={
                <Avatar
                  name={$t({
                    defaultMessage: 'John Smith',
                    id: 'Admin.ManageBadges.Preview.NameSample',
                  })}
                  size="8x"
                  className="ring-4 bg-surface ring-surface ring-offset-0 ring-offset-surface sm:h-32 sm:w-32"
                  fallback={MemberAvatarPlaceholder}
                />
              }
            />
          </Card>
        </div>
        <div className="grid grid-cols-12 gap-5">
          <div className="col-span-8 flex flex-col space-y-5">
            <MemberPostsPlaceholder count={2} member={fakeMember} />
          </div>
          <div className="col-span-4">
            <MemberAbout preview memberId={fakeMember.id} />
          </div>
        </div>
      </div>
      {badgeModal ? (
        <BadgeModal
          badge={badgeModal}
          open
          onClose={() => setBadgeModal(null)}
        />
      ) : null}
    </div>
  )
}
